import React, { useEffect, useRef, useState } from 'react'

import { defaultMultipleSelectionHeaderColumn } from '@/constants/ag-grid-scenario-builder.constant'
import {
  ACCEPTED_FILE_FORMATS_FOR_LIABILITY_REINVESTMENT_TAXES,
  optimizationDefaultColDefs,
} from '@/constants/portfolioOptimization.constant'
import { DEFAULT_FLEX_CENTER } from '@/constants/scenarioBuilder.constant'
import { IPreAfterTaxFileRecord } from '@/interfaces/portfolioOptimization.interface'
import { getPreAfterTaxRatesData } from '@/services/portfolioOptimization.service'
import { portfolioOptimizationDefaultGridOptions } from '@/utils/grid-utils/colDefs'
import { GridOptions } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import DeleteIcon from '@mui/icons-material/Delete'
import { Box, IconButton, Typography } from '@mui/material'
import FileUploadBox from './FileUploadBox'

interface PreAfterTaxRatesGridProps {
  onChange: (fileData: IPreAfterTaxFileRecord[]) => void
}

const PreAfterTaxRatesGrid: React.FC<PreAfterTaxRatesGridProps> = ({
  onChange,
}) => {
  const [fileRecords, setFileRecords] = useState<IPreAfterTaxFileRecord[]>([])
  // Grid Configuration
  const gridRef = useRef<AgGridReact>(null)

  const handleFileSelect = async (file: File | null) => {
    if (file) {
      try {
        // const parsedFileData = await FLWParser.parseFile(file)
        const response = await getPreAfterTaxRatesData()
        setFileRecords(response)
      } catch (error) {
        console.error('Failed to parse .flw file:', error)
      }
    }
  }

  const DeleteCellRenderer = ({ data: selectedRecord }: any) => {
    const onDelete = () => {
      // remove this record
      setFileRecords(
        fileRecords.filter(
          (record) => record.nonMunicipalTax !== selectedRecord.nonMunicipalTax,
        ),
      )
    }
    return (
      <IconButton onClick={onDelete} aria-label='delete'>
        <DeleteIcon />
      </IconButton>
    )
  }

  useEffect(() => {
    onChange(fileRecords)
  }, [fileRecords])

  const gridOptions: GridOptions = {
    ...portfolioOptimizationDefaultGridOptions,
    sideBar: false,
    noRowsOverlayComponentParams: () => {
      return {
        noRowsMessage: 'No data available. Please upload a file.',
      }
    },
    rowSelection: 'single' as const,
    columnDefs: [
      {
        ...defaultMultipleSelectionHeaderColumn,
        checkboxSelection: false,
        cellRenderer: DeleteCellRenderer,
        cellStyle: DEFAULT_FLEX_CENTER,
      },
      {
        ...optimizationDefaultColDefs,
        floatingFilter: false,
        headerName: 'Non Municipal Tax (%)',
        field: 'nonMunicipalTax',
        filter: 'agNumberColumnFilter',
      },
      {
        ...optimizationDefaultColDefs,
        floatingFilter: false,
        headerName: 'Grandfathered (%)',
        field: 'municipalTax.grandfathered',
        filter: 'agNumberColumnFilter',
      },
      {
        ...optimizationDefaultColDefs,
        floatingFilter: false,
        headerName: 'Non-Grandfathered (%)',
        field: 'municipalTax.nonGrandfathered',
        filter: 'agNumberColumnFilter',
      },
      {
        ...optimizationDefaultColDefs,
        floatingFilter: false,
        headerName: 'Zeros (%)',
        field: 'municipalTax.zeros',
        filter: 'agNumberColumnFilter',
      },
    ],
  }

  return (
    <Box flex={1}>
      <Box marginBottom={2}>
        <Typography variant='h5'>Optimization Special Tax Rates</Typography>
        <Typography variant='body2'>
          Upload a File or manually enter the Optimization Special Tax Rates in
          the table below.
        </Typography>
        <FileUploadBox
          id='special-tax-rate-upload'
          onFileSelect={handleFileSelect}
          acceptedFormats={
            ACCEPTED_FILE_FORMATS_FOR_LIABILITY_REINVESTMENT_TAXES
          }
        />
      </Box>
      <Box className='ag-theme-quartz'>
        <AgGridReact
          ref={gridRef}
          rowData={fileRecords}
          gridOptions={gridOptions}
          data-cy='pre-after-tax-rates-ag-grid'
        ></AgGridReact>
      </Box>
    </Box>
  )
}

export default PreAfterTaxRatesGrid
